import React from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  InputAdornment,
  Grid,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Tag,
  Layers,
  Inventory,
  LocationOn,
  Business,
  CheckCircle,
  ArrowRight,
} from '@mui/icons-material';
import { InventoryItem } from '../../../types/inventory';
import { colors } from '../../../styles/theme';
import { useInventoryDrawer } from './InventoryDrawer.hooks';
import { useEscapeKey } from '../../../hooks/useEscapeKey';
import { CloseConfirmationModal } from '../../modals/confirmation';
import { APP_CURRENCY } from '../../../utils/constants/currency';
import { DecimalNumberInput } from '../../ui';
import {
  StyledDrawer,
  DrawerContainer,
  DrawerHeader,
  DrawerTitle,
  CloseButton,
  ProgressContainer,
  ProgressBar,
  ProgressBarFill,
  ProgressInfo,
  StepIndicatorContainer,
  StepIndicator,
  StepItem,
  StepCircle,
  StepConnector,
  StepLabel,
  ContentContainer,
  StepCard,
  StepCardHeader,
  StepCardTitle,
  StepCardContent,
  FieldsGrid,
  FieldLabel,
  StyledTextField,
  StyledFormControl,
  TagsContainer,
  TagChip,
  TagInput,
  DrawerFooter,
  ButtonsContainer,
  RightButtonsContainer,
  PreviousButton,
  NextButton,
  SaveButton,
  ConfirmationModal,
  ConfirmationCard,
  ModalHeader,
  ModalTitle,
  ModalMessage,
  ModalButtonsContainer,
  ModalCancelButton,
  ModalConfirmButton,
} from './InventoryDrawer.styles';

export interface InventoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (item: any) => Promise<void>;
  itemTypes: { id: number; name: string }[];
  itemMaterials: { id: number; name: string }[];
  branches: { id: number; name: string }[];
  itemStatuses: { id: string; name: string }[];
  editItem?: InventoryItem | null;
}

const InventoryDrawer: React.FC<InventoryDrawerProps> = ({
  isOpen,
  onClose,
  onSave,
  itemTypes,
  itemMaterials,
  branches,
  itemStatuses,
  editItem = null,
}) => {
  const {
    currentStep,
    formProgress,
    showConfirmation,
    showCancelConfirmation,
    isSaving,
    selectedTags,
    formData,
    steps,
    nextStep,
    prevStep,
    handleInputChange,
    handleSelectChange,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    addTag,
    removeTag,
    handleTagKeyDown,
    validateCurrentStep,
    handleSave,
    handleCloseClick,
    cancelOperation,
    handleConfirmSave,
    handleCancelSave,
    handleCancelCancel,
  } = useInventoryDrawer({
    isOpen,
    onClose,
    onSave,
    itemTypes,
    itemMaterials,
    branches,
    itemStatuses,
    editItem,
  });

  // Interceptar ESC para mostrar confirmación de cierre
  useEscapeKey(
    () => {
      if (isOpen && !isSaving && !showCancelConfirmation) {
        handleCloseClick();
      }
    },
    isOpen && !isSaving && !showCancelConfirmation,
  );

  // Colores para los pasos
  const stepColors = {
    1: '#7C5DC9', // Violet
    2: '#5084EB', // Blue
    3: '#0CBBDD', // Cyan
    4: '#0CBB9A', // Teal
  };

  // Renderizar contenido del paso
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepCard>
            <StepCardHeader stepColor={stepColors[1]}>
              <Tag />
              <StepCardTitle>Información Básica</StepCardTitle>
            </StepCardHeader>
            <StepCardContent>
              <FieldsGrid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Código *</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="id"
                    value={formData.id}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Ej. INV-001"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Nombre del Producto *</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Ej. Anillo de oro"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Número de Registro</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="recordCode"
                    value={formData.recordCode}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Ej. REG-2023-001"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Fecha de registro</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="createdAt"
                    type="date"
                    value={formData.createdAt}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </FieldsGrid>
            </StepCardContent>
          </StepCard>
        );

      case 2:
        return (
          <StepCard>
            <StepCardHeader stepColor={stepColors[2]}>
              <Layers />
              <StepCardTitle>Categoría</StepCardTitle>
            </StepCardHeader>
            <StepCardContent>
              <FieldsGrid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Tipo de Producto</FieldLabel>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="type"
                      value={formData.type}
                      onChange={handleSelectChange}
                      displayEmpty
                    >
                      <MenuItem value="">Seleccione una categoría</MenuItem>
                      {itemTypes.map((type) => (
                        <MenuItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Material</FieldLabel>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="material"
                      value={formData.material}
                      onChange={handleSelectChange}
                      displayEmpty
                    >
                      <MenuItem value="">Seleccionar material</MenuItem>
                      {itemMaterials.map((material) => (
                        <MenuItem key={material.id} value={material.id.toString()}>
                          {material.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Grid>
              </FieldsGrid>

              <Box sx={{ mb: 3 }}>
                <FieldLabel>Etiquetas</FieldLabel>
                <TagsContainer>
                  {selectedTags.map((tag) => (
                    <TagChip key={tag} label={tag} onDelete={() => removeTag(tag)} size="small" />
                  ))}
                  <TagInput
                    type="text"
                    placeholder="Agregar nueva etiqueta..."
                    onKeyDown={handleTagKeyDown}
                  />
                </TagsContainer>
              </Box>

              <Box>
                <FieldLabel>Precio</FieldLabel>
                <DecimalNumberInput
                  fullWidth
                  name="price"
                  value={formData.price}
                  onChange={(val) =>
                    handleInputChange({
                      target: { name: 'price', value: val != null ? String(val) : '' },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  variant="outlined"
                  size="small"
                  placeholder="0,00"
                  decimalPlaces={2}
                  min={0}
                  max={999999.99}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">{APP_CURRENCY.symbol}</InputAdornment>
                    ),
                  }}
                />
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ display: 'block', mt: 0.5 }}
                >
                  Solo se permiten hasta 2 decimales
                </Typography>
              </Box>
            </StepCardContent>
          </StepCard>
        );

      case 3:
        return (
          <StepCard>
            <StepCardHeader stepColor={stepColors[3]}>
              <LocationOn />
              <StepCardTitle>Ubicación</StepCardTitle>
            </StepCardHeader>
            <StepCardContent>
              <FieldsGrid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Sucursal</FieldLabel>
                  <StyledFormControl fullWidth size="small">
                    <Select
                      name="location"
                      value={formData.location}
                      onChange={handleSelectChange}
                      displayEmpty
                    >
                      <MenuItem value="">Seleccionar sucursal</MenuItem>
                      {branches.map((branch) => (
                        <MenuItem key={branch.id} value={branch.id.toString()}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Ubicación específica</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="locationName"
                    value={formData.locationName}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    placeholder="Ej. Vitrina 3, Estante A"
                  />
                </Grid>
              </FieldsGrid>
            </StepCardContent>
          </StepCard>
        );

      case 4:
        return (
          <StepCard>
            <StepCardHeader stepColor={stepColors[4]}>
              <Business />
              <StepCardTitle>Estado</StepCardTitle>
            </StepCardHeader>
            <StepCardContent>
              <FieldsGrid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Estado actual</FieldLabel>
                  <StyledFormControl fullWidth size="small">
                    <Select name="status" value={formData.status} onChange={handleSelectChange}>
                      {itemStatuses.map((status) => (
                        <MenuItem key={status.id} value={status.name}>
                          {status.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </StyledFormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FieldLabel>Fecha de Transferencia</FieldLabel>
                  <StyledTextField
                    fullWidth
                    name="updatedAt"
                    type="date"
                    value={formData.updatedAt}
                    onChange={handleInputChange}
                    variant="outlined"
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </FieldsGrid>
            </StepCardContent>
          </StepCard>
        );

      default:
        return null;
    }
  };

  // Renderizar indicador de pasos
  const renderStepIndicator = () => {
    return (
      <StepIndicator>
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const stepColor = stepColors[step.id as keyof typeof stepColors];
          const nextStepColor = steps[index + 1]
            ? stepColors[steps[index + 1].id as keyof typeof stepColors]
            : stepColor;

          return (
            <StepItem key={step.id}>
              <StepCircle isActive={isActive} isCompleted={isCompleted} stepColor={stepColor}>
                <Box
                  sx={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    backgroundColor: isActive || isCompleted ? stepColor : colors.grey[300],
                    filter:
                      isActive || isCompleted
                        ? 'drop-shadow(0px 3px 5px rgba(0,0,0,0.25))'
                        : 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'relative',
                    zIndex: 10,
                    transition: 'all 0.3s ease',
                  }}
                >
                  {step.icon === 'Tag' && <Tag />}
                  {step.icon === 'Layers' && <Layers />}
                  {step.icon === 'LocationOn' && <LocationOn />}
                  {step.icon === 'Business' && <Business />}
                </Box>
              </StepCircle>
              <StepLabel isActive={isActive} isCompleted={isCompleted}>
                {step.name}
              </StepLabel>
              {index < steps.length - 1 && (
                <StepConnector
                  isCompleted={isCompleted}
                  fromColor={stepColor}
                  toColor={nextStepColor}
                />
              )}
            </StepItem>
          );
        })}
      </StepIndicator>
    );
  };

  // Modal de confirmación
  const ConfirmationModalComponent: React.FC<{
    onConfirm: () => void;
    onCancel: () => void;
    title?: string;
    message?: string;
    confirmText?: string;
  }> = ({
    onConfirm,
    onCancel,
    title = 'Confirmar acción',
    message = '¿Confirmas guardar este item en el inventario?',
    confirmText = 'Confirmar',
  }) => {
    return (
      <ConfirmationModal>
        <ConfirmationCard>
          <ModalHeader>
            <IconButton>
              <Inventory />
            </IconButton>
            <ModalTitle>{title}</ModalTitle>
          </ModalHeader>
          <ModalMessage>{message}</ModalMessage>
          <ModalButtonsContainer>
            <ModalCancelButton variant="tonal" onClick={onCancel}>
              Volver
            </ModalCancelButton>
            <ModalConfirmButton onClick={onConfirm} disabled={isSaving}>
              {isSaving ? <CircularProgress size={20} /> : confirmText}
            </ModalConfirmButton>
          </ModalButtonsContainer>
        </ConfirmationCard>
      </ConfirmationModal>
    );
  };

  return (
    <StyledDrawer anchor="right" open={isOpen} onClose={handleCloseClick}>
      {/* Modales de confirmación */}
      {showConfirmation && (
        <ConfirmationModalComponent
          onConfirm={handleSave}
          onCancel={handleCancelSave}
          title="Confirmar acción"
          message="¿Confirmas guardar este item en el inventario?"
        />
      )}

      <CloseConfirmationModal
        open={showCancelConfirmation}
        onClose={handleCancelCancel}
        onConfirm={cancelOperation}
      />

      {/* Contenido del drawer */}
      <DrawerContainer>
        {/* Header */}
        <DrawerHeader>
          <DrawerTitle variant="h5">
            {editItem ? 'Editar Item de Inventario' : 'Nuevo Item de Inventario'}
          </DrawerTitle>
          <CloseButton onClick={handleCloseClick}>
            <CloseIcon />
          </CloseButton>
        </DrawerHeader>

        {/* Barra de progreso */}
        <ProgressContainer>
          <ProgressBar>
            <ProgressBarFill style={{ width: `${formProgress}%` }} />
          </ProgressBar>
          <ProgressInfo>
            <span>
              Paso {currentStep} de {steps.length}
            </span>
            <span>{formProgress}% completado</span>
          </ProgressInfo>
        </ProgressContainer>

        {/* Indicador de pasos */}
        <StepIndicatorContainer>{renderStepIndicator()}</StepIndicatorContainer>

        {/* Contenido del paso */}
        <ContentContainer>{renderStepContent()}</ContentContainer>

        {/* Footer con botones de acción */}
        <DrawerFooter>
          <ButtonsContainer>
            <PreviousButton onClick={prevStep} variant="tonal" disabled={currentStep === 1}>
              Anterior
            </PreviousButton>

            <RightButtonsContainer>
              {currentStep === steps.length ? (
                <SaveButton
                  variant="contained"
                  onClick={handleConfirmSave}
                  disabled={!validateCurrentStep()}
                  startIcon={<CheckCircle />}
                >
                  Guardar Item
                </SaveButton>
              ) : (
                <NextButton
                  onClick={nextStep}
                  variant="contained"
                  disabled={!validateCurrentStep()}
                  endIcon={<ArrowRight />}
                >
                  Siguiente
                </NextButton>
              )}
            </RightButtonsContainer>
          </ButtonsContainer>
        </DrawerFooter>
      </DrawerContainer>
    </StyledDrawer>
  );
};

export default InventoryDrawer;
